/**
 * Tiny zustand-free cart store backed by localStorage. Used by the Shop
 * pages to keep the cart in sync across product list / detail / checkout.
 */
const STORAGE_KEY = "easy-gutachter-cart-v1";

const safeParse = (s) => { try { return s ? JSON.parse(s) : []; } catch { return []; } };

const listeners = new Set();

let state = typeof window !== "undefined" ? safeParse(localStorage.getItem(STORAGE_KEY)) : [];

const persist = () => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* noop */ }
  listeners.forEach((fn) => fn(state));
};

export const cart = {
  get items() { return state; },

  count() { return state.reduce((s, i) => s + Number(i.qty || 0), 0); },

  subtotalCents() {
    return state.reduce((s, i) => s + Number(i.unit_price_cents || 0) * Number(i.qty || 0), 0);
  },

  /** Add a product to the cart. If a line with the same product+size+color
   * already exists, increase its qty instead. */
  add(product, { qty = 1, size = null, color = null } = {}) {
    const lineKey = `${product.id}__${size || ""}__${color || ""}`;
    const existing = state.find((i) => i.lineKey === lineKey);
    if (existing) {
      existing.qty = Math.max(product.min_qty || 1, Number(existing.qty) + Number(qty));
    } else {
      state = [...state, {
        lineKey,
        product_id: product.id,
        sku: product.sku,
        name: product.name,
        unit_price_cents: product.price_cents,
        unit_label: product.unit_label,
        icon: product.icon,
        color: product.color,
        qty: Math.max(product.min_qty || 1, Number(qty) || 1),
        size, color_choice: color,
      }];
    }
    persist();
  },

  setQty(lineKey, qty) {
    const item = state.find((i) => i.lineKey === lineKey);
    if (item) {
      item.qty = Math.max(1, Number(qty) || 1);
      persist();
    }
  },

  remove(lineKey) {
    state = state.filter((i) => i.lineKey !== lineKey);
    persist();
  },

  clear() { state = []; persist(); },

  /** Subscribe to changes — returns unsubscribe fn. */
  subscribe(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
};

export const fmtEuro = (cents) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" })
    .format((Number(cents) || 0) / 100);
